import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowLeft, PackageOpen, ImageIcon, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { toast } from "sonner";

const empty = { name: "", description: "", price: "", image_url: "", active: true };

function ProductForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  const change = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    const price = Number(form.price);
    if (!form.name.trim()) return toast.error("Enter product name");
    if (!(price > 0)) return toast.error("Enter a valid price");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || "",
        price,
        image_url: form.image_url || "",
        active: !!form.active,
      };
      if (isEdit) {
        await api.put(`/admin/products/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="admin-product-form"
      onSubmit={submit}
      className="rounded-2xl border border-white/10 bg-zinc-950/70 p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-emerald-400 font-bold text-lg"
            style={{ fontFamily: "Outfit, sans-serif" }}>
          {isEdit ? "Edit Product" : "Add Product"}
        </h3>
        <button type="button" onClick={onCancel}
                className="p-1.5 text-white/60 hover:text-white">
          <X size={18} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-widest text-white/60 font-semibold">Name</label>
          <input data-testid="product-name-input" value={form.name} onChange={change("name")}
                 className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-emerald-400/60 outline-none" required />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60 font-semibold">Price (₹)</label>
          <input data-testid="product-price-input" type="number" step="0.01" min="1"
                 value={form.price} onChange={change("price")}
                 className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-emerald-400/60 outline-none" required />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60 font-semibold">Image URL</label>
          <input data-testid="product-image-input" value={form.image_url} onChange={change("image_url")}
                 placeholder="https://..."
                 className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-emerald-400/60 outline-none" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-widest text-white/60 font-semibold">Description</label>
          <textarea data-testid="product-desc-input" rows={3}
                    value={form.description} onChange={change("description")}
                    className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-emerald-400/60 outline-none" />
        </div>
        <label className="flex items-center gap-2 text-sm text-white/80">
          <input data-testid="product-active-input" type="checkbox"
                 checked={!!form.active} onChange={change("active")}
                 className="accent-emerald-400" />
          Active (visible to users)
        </label>
      </div>
      <div className="flex items-center gap-2 justify-end pt-2">
        <button type="button" onClick={onCancel}
                className="text-sm text-white/70 hover:text-white px-3 py-2">Cancel</button>
        <button
          data-testid="product-save-btn"
          type="submit"
          disabled={saving}
          className="text-sm bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-4 py-2 rounded-lg"
        >
          {saving ? "Saving..." : isEdit ? "Save changes" : "Add product"}
        </button>
      </div>
    </form>
  );
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product or null
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#050506" }}>
      <AdminHeader title="Products" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/admin/dashboard"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-1 text-emerald-400 text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          <button
            data-testid="admin-add-product-btn"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm px-4 py-2 rounded-lg"
          >
            <Plus size={16} /> Add Product
          </button>
        </div>

        {showForm && (
          <ProductForm
            initial={editing}
            onCancel={() => { setShowForm(false); setEditing(null); }}
            onSaved={() => { setShowForm(false); setEditing(null); load(); }}
          />
        )}

        {loading ? (
          <div className="py-14 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-emerald-400 border-t-transparent animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-zinc-950/70 p-10 text-center text-white/60">
            <PackageOpen size={40} className="mx-auto text-white/30 mb-2" />
            No products yet. Click <b>Add Product</b> to create one.
          </div>
        ) : (
          <div data-testid="admin-products-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((p) => (
              <article
                key={p.id}
                data-testid={`admin-product-${p.id}`}
                className="rounded-2xl border border-white/10 bg-zinc-950/60 overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/10] bg-zinc-900">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30">
                      <ImageIcon size={36} />
                    </div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-white leading-snug"
                        style={{ fontFamily: "Outfit, sans-serif" }}>
                      {p.name}
                    </h3>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                      p.active
                        ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                        : "text-white/60 border-white/10 bg-white/5"
                    }`}>
                      {p.active ? "Active" : "Hidden"}
                    </span>
                  </div>
                  <p className="text-xs text-white/70 mt-1 flex-1">{p.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-emerald-400 text-xl font-black"
                         style={{ fontFamily: "Outfit, sans-serif" }}>
                      ₹{Number(p.price).toLocaleString("en-IN")}
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid={`edit-product-${p.id}`}
                        onClick={() => { setEditing(p); setShowForm(true); }}
                        className="p-2 rounded-lg border border-white/10 hover:bg-white/5"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`delete-product-${p.id}`}
                        onClick={() => del(p)}
                        className="p-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
